// Durable orphan→authoritative capture_sessions re-home mapping.
//
// When a createSession PUT collides (409 already_exists) because the user
// already has an active server session for the (user, building) that hadn't
// synced down yet, the carve-out (connector-dispatch-session-insert.ts) re-homes
// the orphan's CHILDREN onto the authoritative session and DELETEs the local
// orphan row. But a complete/reopen the surveyor tapped OFFLINE — before the
// reconnect re-home ran — was queued as a capture_sessions PATCH against the
// ORPHAN id. Without a redirect that PATCH PUTs the now-deleted id → 404.
// dispatchSessionPatch redirects the toggle onto the authoritative session id,
// AT the authoritative syncVersion (so a version-mismatched complete does not
// 412 and get swallowed — Codex on PR 660).
//
// DURABILITY (Codex on PR 660). The mapping is persisted in the local-only
// `session_rehomes` table, not only a module Map. PowerSync caps getCrudBatch at
// 100 entries, so a >100-op offline session can upload the colliding INSERT in
// one batch and its queued complete in a LATER batch — possibly across an app
// reload / background-kill, which a Map would not survive (the complete would
// then 404 on the dead orphan and be acked, leaving the building wrong). The
// in-memory Map is a fast synchronous cache for reads within a run;
// hydrateSessionRehomes repopulates it from the table after a reload. Writes go
// through the table FIRST (durable before the INSERT is acked), then the cache.
//
// Keys are client-generated session UUIDs (globally unique), so a stale entry
// can never mis-match a different session.

import * as Sentry from '@sentry/react'

import type { PowerSyncWriteDb } from './powersync-db'

import { powerSyncRowExists } from './powersync-local-db-utils'

/** An authoritative re-home target: the live session id + its server version. */
export interface SessionRehomeTarget {
  readonly authoritativeId: string
  /** Authoritative syncVersion from the 409 details, or null if none supplied. */
  readonly syncVersion: number | null
}

// Synchronous read cache. Durability lives in `session_rehomes`;
// hydrateSessionRehomes refills this from the table after a reload.
const sessionRehomes = new Map<string, SessionRehomeTarget>()

// Consecutive hydrateSessionRehomes read failures. Bounds the retry below so a
// PERSISTENTLY unreadable `session_rehomes` table can't wedge the whole
// count-upload queue forever — a transient lock clears in a cycle or two; a
// dead table eventually falls through to the cold-cache best-effort path.
let consecutiveHydrateFailures = 0

// Max consecutive uploadData cycles a hydrate read failure may force a retry
// before we give up and proceed with a cold cache. Small on purpose: the
// realistic failure is a momentary SQLite lock at process start that clears
// almost immediately, and a larger bound would only delay unrelated count
// uploads longer in the pathological persistent-failure case.
const MAX_HYDRATE_RETRY_CYCLES = 3

/**
 * Record orphan session id → authoritative target DURABLY (set by the
 * createSession carve-out BEFORE it acks the INSERT). Writes the table first so
 * the mapping survives a reload even if the app dies right after, then updates
 * the cache. Idempotent on the orphan id. `syncVersion` is the authoritative
 * session's version from the 409 details (null when the backend supplied none —
 * the redirect then falls back to the orphan's own version).
 */
export async function recordSessionRehome(
  db: PowerSyncWriteDb,
  orphanId: string,
  authoritativeId: string,
  syncVersion: number | null,
): Promise<void> {
  // VIEW-SAFE write, NOT an upsert. PowerSync exposes every table (local-only
  // included) as a SQLite VIEW, and SQLite rejects `INSERT … ON CONFLICT` on a
  // view with "cannot UPSERT a view" — which would throw here and re-wedge the
  // very upload queue this re-home exists to keep draining. So we read-then-
  // INSERT-or-UPDATE instead. The orphan id is a globally-unique client session
  // UUID and only 409-collides once per (user, building), so re-records are rare;
  // the UPDATE branch keeps it idempotent when it does happen. (The session_rehomes
  // mapping row itself is NOT deleted on re-home — only the orphan capture_sessions
  // row is; the mapping persists so a later-batch complete still redirects.)
  // powerSyncRowExists feature-detects getOptional (execute-only unit stubs fall
  // back to the INSERT path) and PROPAGATES a read error rather than swallowing
  // it — a transient SELECT failure must not route a re-record to an INSERT that
  // PK-conflicts on the existing row and wedges the queue (audit-1 #1).
  if (await powerSyncRowExists(
    db,
    `SELECT id FROM session_rehomes WHERE id = ? LIMIT 1`,
    [orphanId],
  )) {
    await db.execute(
      `UPDATE session_rehomes SET
         authoritative_id = ?,
         authoritative_sync_version = ?,
         recorded_at = strftime('%Y-%m-%dT%H:%M:%fZ','now')
       WHERE id = ?`,
      [authoritativeId, syncVersion, orphanId],
    )
  } else {
    await db.execute(
      `INSERT INTO session_rehomes
         (id, authoritative_id, authoritative_sync_version, recorded_at)
       VALUES (?, ?, ?, strftime('%Y-%m-%dT%H:%M:%fZ','now'))`,
      [orphanId, authoritativeId, syncVersion],
    )
  }
  sessionRehomes.set(orphanId, { authoritativeId, syncVersion })
}

/**
 * Look up the authoritative re-home target for an orphan id, or undefined if it
 * was never re-homed. Synchronous cache read — call hydrateSessionRehomes once
 * per upload cycle first so a re-home recorded before an app reload is visible.
 */
export function getSessionRehome(
  orphanId: string,
): SessionRehomeTarget | undefined {
  return sessionRehomes.get(orphanId)
}

/**
 * Refill the cache from the durable table at the start of each upload cycle, so
 * a re-home persisted before an app reload redirects correctly.
 *
 * The session-toggle dispatchers resolve the orphan→authoritative redirect
 * SOLELY via getSessionRehome — complete/reopen redirects onto the authoritative
 * session (connector-dispatch-session.ts), cancel PRESERVES it
 * (connector-dispatch-session-cancel.ts). A COLD cache here makes a cross-reload
 * complete/reopen PUT the now-deleted orphan id → 404 → ack-DROP the surveyor's
 * completion intent (logged, but a real dropped toggle). Unlike the count path
 * (connector-dispatch-create-item.ts) these have no building-lookup fallback —
 * and cancel deliberately must NOT redirect a destructive delete onto the
 * authoritative session — so this cache is the only redirect source.
 *
 * Therefore a read failure does NOT proceed cold: we RE-ENTER the upload cycle
 * (throw → the SDK re-runs uploadData). hydrate runs BEFORE any entry dispatches
 * (connector.ts), so nothing is half-applied — the re-run just gives a transient
 * SQLite lock a cycle to clear, after which getSessionRehome hits. The retry is
 * BOUNDED (MAX_HYDRATE_RETRY_CYCLES): a persistently unreadable table must not
 * wedge the count-upload queue forever (the never-wedge property uploadData
 * relies on), so after the bound we surface the give-up (Sentry) and fall back
 * to the cold-cache best-effort path — the in-run cache still serves
 * current-session re-homes.
 */
export async function hydrateSessionRehomes(
  db: PowerSyncWriteDb,
): Promise<void> {
  const getAll = (db as { getAll?: (sql: string) => Promise<unknown> }).getAll
  if (typeof getAll !== 'function') return
  let rows: unknown
  try {
    rows = await getAll.call(
      db,
      `SELECT id, authoritative_id, authoritative_sync_version FROM session_rehomes`,
    )
  } catch (err) {
    consecutiveHydrateFailures += 1
    if (consecutiveHydrateFailures <= MAX_HYDRATE_RETRY_CYCLES) {
      // Re-enter uploadData rather than redirect off a cold cache. Rethrow the
      // raw read error: hydrate runs before the dispatch loop, so this
      // propagates straight out of uploadData (no per-entry poison-isolation)
      // and the SDK retries the whole batch on its next tick.
      throw err
    }
    // Bound exhausted — the table is persistently unreadable. Stop retrying so
    // the queue can drain; surface the give-up so the degraded redirect (a
    // pre-reload re-homed complete/reopen/cancel may now miss) isn't silent.
    Sentry.captureMessage(
      '[powersync] hydrateSessionRehomes read kept failing — proceeding with a cold re-home cache; a complete/reopen/cancel against a pre-reload re-homed orphan may not redirect',
      {
        level: 'warning',
        extra: {
          reason: 'powersync_hydrate_session_rehomes_cold_fallback',
          consecutiveFailures: consecutiveHydrateFailures,
        },
      },
    )
    return
  }
  // Read succeeded — clear the failure budget, then refill the cache.
  consecutiveHydrateFailures = 0
  if (!Array.isArray(rows)) return
  for (const raw of rows) {
    const row = raw as {
      id?: unknown
      authoritative_id?: unknown
      authoritative_sync_version?: unknown
    }
    if (typeof row.id !== 'string' || typeof row.authoritative_id !== 'string') {
      continue
    }
    sessionRehomes.set(row.id, {
      authoritativeId: row.authoritative_id,
      syncVersion:
        typeof row.authoritative_sync_version === 'number'
          ? row.authoritative_sync_version
          : null,
    })
  }
}

/** Test-only reset of the read cache + retry budget so cases don't leak across `it` blocks. */
export function _resetSessionRehomes(): void {
  sessionRehomes.clear()
  consecutiveHydrateFailures = 0
}
